/**
 * `useSession` — fetch + cache `/api/me` for the SPA shell
 * (web UI Phase 2 chunk 4/6).
 *
 * Semantics:
 *   - `loading: true` while the first `/api/me` is in flight.
 *   - 200 → `session: { captain_id, team_id, role }`, `loading: false`.
 *   - 401 → `session: null` (NOT an error — that's just the unauthed state).
 *   - any other status / network failure → `error: <ApiError|Error>`.
 *
 * No automatic refetch on focus or interval. Callers that need that
 * (e.g. polling after sign-out) call `refetch()` explicitly.
 *
 * Why this isn't React Query / SWR: we have a single dependency and one
 * call site (`<RequireAuth>`). Adding a 12 KB library to gain features
 * we'd ignore is the wrong tradeoff at Phase 2 chunk 4 surface area.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, apiGet } from "./api";

export type Session = {
  captain_id: string;
  team_id: string;
  role: "head" | "co";
};

type MeResponse = {
  ok: true;
  captain_id: string;
  team_id: string;
  role: "head" | "co";
};

export type UseSessionState = {
  session: Session | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
};

export function useSession(): UseSessionState {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // Bumping this triggers a refetch via the useEffect dep below.
  const [reloadKey, setReloadKey] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await apiGet<MeResponse>("/api/me");
        if (cancelled || !mountedRef.current) return;
        setSession({
          captain_id: data.captain_id,
          team_id: data.team_id,
          role: data.role,
        });
      } catch (e) {
        if (cancelled || !mountedRef.current) return;
        if (e instanceof ApiError && e.status === 401) {
          setSession(null);
        } else {
          setSession(null);
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        if (cancelled || !mountedRef.current) return;
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const refetch = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  return { session, loading, error, refetch };
}
